/**
 * Central cache-policy registry.
 *
 * Instead of setting staleTime / refetchOnMount on every one of the hundreds of
 * query hooks, we register per-key-prefix defaults once. React Query resolves
 * defaults by prefix (structural partial match), so `setQueryDefaults(['claims'])`
 * applies to `useQuery({ queryKey: ['claims', params] })`. Options passed by an
 * individual hook always win over these defaults, so this is backward-compatible
 * - it only sets a floor for hooks that don't already specify the option.
 *
 * Profiles (see docs/frontend-caching.md for the full matrix):
 * - LIST_LIVE: paint cached rows instantly, but ALWAYS fire a request on every
 *   mount regardless of TTL (`refetchOnMount: 'always'`). Lists that must stay live.
 * - DETAIL:    stale-while-revalidate object detail; refetch only if stale (2 min).
 * - REFERENCE: config/reference data; NO server request until the 60-min TTL expires.
 * - CONTEXT:   identity/permission-bearing; short TTL, never persisted (see persist-policy).
 *
 * Persistence (what is written to IndexedDB) is governed separately by
 * `shouldPersistQuery` in persist-policy.ts.
 */

import type { QueryClient } from '@tanstack/react-query'

const MINUTE = 60_000

interface CachePolicy {
  staleTime: number
  gcTime: number
  refetchOnMount: boolean | 'always'
  refetchOnWindowFocus: boolean
}

const LIST_LIVE: CachePolicy = {
  staleTime: 30_000,
  gcTime: 30 * MINUTE,
  // 'always' guarantees a background request on every navigation-to-page,
  // independent of staleTime. Cached rows still paint first (isLoading stays
  // false while data is present), so there is no loading flash.
  refetchOnMount: 'always',
  refetchOnWindowFocus: true,
}

const DETAIL: CachePolicy = {
  staleTime: 2 * MINUTE,
  gcTime: 30 * MINUTE,
  // Paint the cached (possibly persisted) record instantly, but ALWAYS refetch
  // on mount so a detail page reflects the latest server state - e.g. after an
  // edit-then-reload, or a status change made elsewhere. Without this, a
  // freshly-persisted record would be treated as fresh and never refreshed,
  // showing stale data until staleTime elapsed.
  refetchOnMount: 'always',
  refetchOnWindowFocus: true,
}

const REFERENCE: CachePolicy = {
  staleTime: 60 * MINUTE,
  gcTime: 2 * 60 * MINUTE,
  refetchOnMount: true,
  refetchOnWindowFocus: false,
}

// Search / type-ahead lookups (product & entity pickers, address autocomplete).
// Results are cached so re-opening a picker or repeating a term paints from
// cache instead of re-hitting the server. Used by imperative
// `queryClient.fetchQuery` calls in the resource-lookup providers, which reuse
// this staleTime to decide cache-hit vs network.
const SEARCH: CachePolicy = {
  staleTime: 5 * MINUTE,
  gcTime: 30 * MINUTE,
  refetchOnMount: true,
  refetchOnWindowFocus: false,
}

const CONTEXT: CachePolicy = {
  staleTime: MINUTE,
  gcTime: 30 * MINUTE,
  refetchOnMount: true,
  refetchOnWindowFocus: true,
}

// Aggregate status-pill counts for a list (e.g. registrations). These are an
// expensive whole-tenant scan, so - unlike the LIST_LIVE rows they accompany -
// they must NOT refetch on every mount/focus. Cached counts paint instantly and
// revalidate only once stale (60s); acceptable staleness for display counts.
const COUNTS: CachePolicy = {
  staleTime: 60_000,
  gcTime: 30 * MINUTE,
  refetchOnMount: true,
  refetchOnWindowFocus: false,
}

/**
 * Top-level query-key prefixes per profile. Keep one entry per top-level key;
 * do not register overlapping prefixes (React Query would merge multiple
 * matches in map-iteration order).
 */
const PROFILE_PREFIXES: Record<string, readonly string[]> = {
  LIST_LIVE: [
    'claims',
    // Forms: templates (Settings and the Send dialog) and the forms sent
    // about one record. Both change from other screens, so refetch on mount.
    'form-templates',
    'form-requests',
    // Other customers at a customer's address: another record changing moves
    // it, so it refetches on every mount.
    'customer-address-matches',
    'customer-address-check',
    'holdings',
    'transfers',
    'inspections',
    // Inspection evidence aggregated onto a linked claim. Distinct top-level
    // key from 'inspections' (exact first-element match, no overlap); LIST_LIVE
    // so newly-uploaded inspection documents appear on the claim on next mount.
    'inspections-claim-evidence',
    'lab-queue',
    'lab-tasks',
    'products',
    'entity-records',
    'claim-children',
    'sms-templates',
    // Registration wizard product-picker list. LIST_LIVE (not REFERENCE) so
    // `refetchOnMount: 'always'` revalidates every mount - a cached empty page
    // self-corrects while cached rows still paint first.
    'published-products',
    // A list admins CREATE INTO, so it cannot be reference data: under
    // REFERENCE it only refetched once stale, and the list is persisted to
    // IndexedDB, so adding a template then reloading restored a pre-create
    // snapshot that counted as fresh and fired no request - the new template
    // was simply missing. Same reasoning as the DETAIL profile's comment.
    'product-templates',
    'inspection-itineraries',
    // Computed contractor-community summary (Pool table). Refreshed on every
    // mount so counts reflect the latest inspection/registration state.
    'contractor-summary',
    'inspection-itinerary-suggestions',
    // Network people. A list admins act on (grant / revoke / move access), so
    // LIST_LIVE: cached rows paint first and every mount revalidates, which is
    // what makes a just-granted person show the right access state.
    'network-people',
    // Staff cache observability: volatile cross-tenant Redis operational data.
    // LIST_LIVE so it refetches on every mount; also NEVER_PERSIST (see
    // persist-policy.ts) so it is never written to IndexedDB.
    'staff-cache-stats',
    // The property record's Notes roll-up, one request covering every product
    // registered against the property. LIST_LIVE because staff WRITE into it
    // from the product record: cached rows paint first, and every mount
    // revalidates, so a note just authored on a product is present the moment
    // the summary is opened rather than after the TTL expires.
    'property-notes',
    // A record's own Notes list. Written from outside the panel too (a sent
    // letter files its note), so senders invalidate it and every mount refetches.
    'record-notes',
    'staff-cache-keys',
    // Communications activity: every message sent about a record, plus the ones
    // that were skipped. LIST_LIVE so a page that was cached empty (a quiet
    // tenant, or a record with no comms yet) refires on every mount and
    // self-corrects, while cached rows still paint first.
    'communications-activity',
    'communications-activity-summary',
    // Upstream org product roster for the partner-invite dialog. LIST_LIVE so a
    // newly-published product shows up on the next open of the invite modal.
    'partnership-product-roster',
    // Partnerships / Network list (search-filterable). A list admins CREATE
    // INTO (invite a partner), so LIST_LIVE not REFERENCE: cached rows paint
    // first, but every mount revalidates so a just-added partner is never
    // missing behind a persisted pre-create snapshot.
    'partnerships',
    // Outstanding invitations for a partner org, shown on the partner page. A
    // list admins CREATE INTO (send/resend an invite), so LIST_LIVE: a
    // just-sent invite must never be missing behind a persisted snapshot.
    'partnership-invitations',
    // Variant rows expanded under a product in the library. A tenant-scoped
    // sublist keyed by product + term; LIST_LIVE so it revalidates on mount
    // while cached rows paint first.
    'product-variants',
    // Loaded values on a product or variant. LIST_LIVE so a just-loaded
    // batch appears after navigation without waiting on a long TTL.
    'value-stock',
    // Inspection pool / queue list (search + filter). LIST_LIVE so a page that
    // was cached empty refires on every mount and self-corrects.
    'inspection-pool',
    // Bulk-upload claim types for the import picker. Enabling a type is a
    // server-side change the client never sees, so an empty list cached before
    // it was switched on persisted to IndexedDB and kept telling the user no
    // types were enabled. LIST_LIVE revalidates every time the dialog opens.
    'bulk-import-targets',
    // Identification facets for the Test VA dialog. Same reasoning as
    // bulk-import-targets: flagging an attribute as a facet is a server-side
    // change the client never sees, so a list cached before it was switched on
    // would persist and keep the dialog showing no product fields.
    'test-va-facets',
    // Geography reference data (WPS-222). Every one of these is a list admins
    // CREATE INTO from the Geography tab, so LIST_LIVE not REFERENCE - the same
    // pre-create-snapshot trap product-templates hit. `geo-zones` /
    // `geo-zones-all` moved here from REFERENCE for the same reason (the tab
    // now edits zones in place). `product-availability` is the per-product
    // list the cross-product tab also writes to.
    'geo-zones',
    'geo-zones-all',
    'geo-postal-codes',
    'geo-psa-mappings',
    'geo-state-defaults',
    'geo-availability',
    'geo-assignments',
    'geo-assignment-members',
    'product-availability',
  ],
  DETAIL: [
    // One partner person. DETAIL, not REFERENCE: their access changes when an
    // admin grants or revokes, so an hour-long persisted TTL would paint a
    // stale state after the very action that changed it.
    'network-person',
    'claim',
    'holding',
    'product',
    'transfer',
    'inspection',
    'inspection-itinerary',
    'warranty-template',
    'claim-schema',
    'transaction-template',
    'host-template',
    'product-template',
    // One message's rendered body + delivery trail. Singular key = detail.
    'communication-activity',
    // The tenant's design-token overrides, read by the Appearance modal. NOT
    // REFERENCE: that profile's 60-min TTL is persisted to IndexedDB, so after
    // the theme changed elsewhere (a staff edit, a deploy reseeding the tenant)
    // the modal painted stale values for up to an hour. DETAIL's
    // `refetchOnMount: 'always'` revalidates on every open while the cached
    // values still paint instantly.
    'org-theme',
    // The tenant's welcome (landing) page config, read by the Settings >
    // Welcome page tab. Same reasoning as `org-theme`: revalidate on open so a
    // change elsewhere (or a deploy reseeding the tenant) is never stale.
    'org-welcome-page',
  ],
  REFERENCE: [
    // Pricing filter facets (distinct product lines / effective dates / work
    // types). Config-like reference data; mutations invalidate these keys so a
    // long TTL never serves a stale option list after an edit.
    'material-pricing-facets',
    'labor-rate-facets',
    // Singular, but NOT a detail page. One entity's definition is ambient
    // config read by many components on every screen (registration list, claim
    // page, detail cards), not a record a user opens. Under DETAIL its
    // `refetchOnMount: 'always'` fired once per mounting component - measured
    // at 8 requests for `holding` in a single production session. Writes call
    // `invalidateQueries(ENTITY_TYPE_KEY(...))` (use-entities.ts), so a long
    // TTL never serves a stale definition after an edit.
    'entity-type',
    'entity-types',
    'warranty-templates',
    'host-templates',
    'transaction-templates',
    'claim-schemas',
    'workflow-definitions',
    'claim-workflows',
    'product-categories',
    'product-brands',
    'warranty-categories',
    'warranty-mappings',
    'proration-formulas',
    // Geography option lists + Census reference tables (WPS-222): config-like,
    // rarely change; the import and CRUD mutations invalidate them explicitly.
    'geo-meta',
    'geo-counties',
    'geo-msas',
    'geo-fips-county-zones',
    'material-pricing-rates',
    'labor-rates',
    'translation-keys',
    'translation-languages',
    'table-views',
    'acl-grants',
    'identifier-config',
    // Wizard metadata that is pure tenant/template config: the registration
    // form schema (fields/host/transaction, keyed per product template) and
    // the create-claim metadata (schema/workflow, keyed per schema+template).
    // Both are prefetched by the post-login warmer and change rarely, so hold
    // the 60-min REFERENCE TTL and paint from cache on wizard open.
    'holding-form-schema',
    'create-claim-metadata',
  ],
  SEARCH: [
    'resource-search',
    'address-autocomplete',
    'address-resolve',
    // The wizard's "find a product" global search box (and the warranty
    // policy-mapping product picker). SEARCH holds each debounced term's
    // result for 5 min and repaints it instantly when a term is retyped,
    // but still revalidates on mount so a stale/empty term never masks
    // live matches.
    'product-search',
    // Host search type-ahead (address / ZIP / owner / vehicle-id lookup in the
    // host picker). Debounced, search-as-you-type: SEARCH so each term's result
    // repaints instantly on re-type instead of re-hitting the server.
    'host-search',
    // Inspector type-ahead (assign-inspector pickers across the inspection
    // wizard, scheduling console, and detail pages). Search-as-you-type keyed by
    // term; SEARCH so repeated terms paint from cache.
    'inspection-inspectors',
  ],
  CONTEXT: [
    'org-context',
    'session',
    'staff',
    // Regroup preview: a simulation of a move that has not happened. Short TTL
    // and never persisted - it drives a confirm step that changes who can see
    // what, and a preview restored from disk could describe a structure that
    // has since moved on.
    'partnership-regroup-preview',
    // A merge's live status. Short TTL and never persisted: a status restored
    // from disk would paint "running" for a job that finished hours ago, and
    // whether the work is still moving is the one thing an operator must be
    // able to trust here.
    'merge-plan',
    'merge-impact',
  ],
  // Aggregate list counts: cached, revalidate-on-stale, never refetch-on-focus.
  COUNTS: [
    // Access-state pill counts. A whole-tenant aggregate (its own endpoint for
    // exactly this reason), so it must NOT ride the LIST_LIVE rows it sits
    // above - 60s-stale display counts are fine.
    'network-people-counts',
    'holding-status-counts',
    'claims-status-counts',
    'claims-assigned-badge',
    'claims-pill-counts',
    // Partner-network health. The scan is a stored artefact that only changes
    // when a scan completes, so refetching on every mount would be pure cost -
    // and a full pass takes seconds over a large book.
    'partnership-health',
    'partnership-health-findings',
    'partnership-health-duplicates',
  ],
}

const PROFILES: Record<string, CachePolicy> = {
  LIST_LIVE,
  DETAIL,
  REFERENCE,
  SEARCH,
  CONTEXT,
  COUNTS,
}

/**
 * Register the cache-policy defaults on the QueryClient. Call once at module
 * scope, immediately after the QueryClient is constructed and before any
 * component mounts (defaults are only picked up by the first query of a key).
 */
export function registerQueryDefaults(queryClient: QueryClient): void {
  for (const [profileName, prefixes] of Object.entries(PROFILE_PREFIXES)) {
    const policy = PROFILES[profileName]
    for (const prefix of prefixes) {
      queryClient.setQueryDefaults([prefix], policy)
    }
  }
}
